import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Check, Code2, Copy, CreditCard, InfoIcon, LoaderCircle, Mail } from 'lucide-react';

import { useCreateAPICredential } from '@/api/APICredentials/useCreateAPICredential';
import { backendRequestHeaders, getBackendBaseUrl } from '@/api/backendAuth';
import { useOnboarding } from '@/api/onboarding/onboarding';
import { useGetWorkspacesByTenant } from '@/api/workspaces/useGetWorkspacesByTenant';
import { brandColors } from '@/app/constants';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { clearOnboardingCache } from '@/component-library/layouts/ProtectedLayout/ProtectedLayout';
import { Button } from '@/component-library/primitives/Button/Button';
import { Input } from '@/component-library/widgets/Input/Input';
import { StripeOAuthButton } from '@/component-library/widgets/StripeOAuthButton/StripeOAuthButton';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace, setSelectedWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';
import { AppRoutes } from '@/utils/routes/router';

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const onboarding = useOnboarding();
  const dispatch = useAppDispatch();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const { data: workspaces, refetch: refetchWorkspaces } = useGetWorkspacesByTenant();
  const { mutateAsync: createAPICredentialMutate } = useCreateAPICredential();
  const { signOut, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [formData, setFormData] = useState({
    workspaceName: '',
    stripeConnected: false,
    stripeAccountId: '',
    sdkKey: '',
    techEmail: '',
  });
  const [copiedSdk, setCopiedSdk] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCompletingOnboarding, setIsCompletingOnboarding] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const hasCheckedStatusRef = useRef<boolean>(false);
  const hasTriedGeneratingKeyRef = useRef<boolean>(false);

  const steps = [
    { id: 1, title: 'Workspace Setup', icon: Building2 },
    { id: 2, title: 'Stripe Connection', icon: CreditCard },
    { id: 3, title: 'SDK Setup', icon: Code2 },
  ];

  // Handle Stripe OAuth callback success state
  const hasHandledStripeCallbackRef = useRef(false);

  useEffect(() => {
    if (location.state?.stripeConnected && location.state?.stripeAccountId && !hasHandledStripeCallbackRef.current) {
      hasHandledStripeCallbackRef.current = true;

      console.log('🔄 Stripe OAuth callback detected');
      console.log('📍 Current workspace before restore:', workspace?.id);

      setFormData((prev) => ({
        ...prev,
        stripeConnected: true,
        stripeAccountId: location.state.stripeAccountId,
      }));

      const ensureWorkspaceAndMoveToStep3 = async () => {
        // ALWAYS try to restore from sessionStorage first to maintain the same workspace
        const storedWorkspace = sessionStorage.getItem('workspace');
        console.log(
          '💾 Stored workspace from sessionStorage:',
          storedWorkspace ? JSON.parse(storedWorkspace).id : 'NONE',
        );

        if (storedWorkspace) {
          try {
            const parsed = JSON.parse(storedWorkspace);
            console.log('✅ Restoring workspace from sessionStorage:', parsed.id);
            dispatch(setSelectedWorkspace(parsed));
          } catch (error) {
            console.error('❌ Failed to parse stored workspace:', error);
          }
        } else {
          // Only fetch if no stored workspace (shouldn't happen)
          console.warn('⚠️ No workspace in sessionStorage! Fetching...');
          const { data: fetchedWorkspaces } = await refetchWorkspaces();
          if (fetchedWorkspaces && fetchedWorkspaces.length > 0) {
            const defaultWorkspace = fetchedWorkspaces[0];
            console.log('📦 Using fetched workspace:', defaultWorkspace.id);
            dispatch(setSelectedWorkspace(defaultWorkspace));
            sessionStorage.setItem('workspace', JSON.stringify(defaultWorkspace));
          }
        }

        console.log('➡️ Moving to step 3 after Stripe OAuth');
        setCurrentStep(3);
      };

      ensureWorkspaceAndMoveToStep3();
      window.history.replaceState({}, document.title);
    }
  }, [location.state, dispatch, refetchWorkspaces, workspace]);

  // Auto-generate API key when reaching step 3 (only once)
  useEffect(() => {
    if (currentStep === 3 && !formData.sdkKey && !isGeneratingKey && !hasTriedGeneratingKeyRef.current) {
      setTimeout(() => handleGenerateAPIKey(), 100);
    }

    // Reset the ref if we leave step 3
    if (currentStep !== 3) {
      hasTriedGeneratingKeyRef.current = false;
    }
  }, [currentStep]);

  // Check if user has already completed onboarding - only check once on mount
  useEffect(() => {
    // Only check once, not on every workspace/workspaces change
    if (hasCheckedStatusRef.current) {
      return;
    }

    // Ensure default workspace is selected (same behavior as other pages)
    if (!workspace) {
      const storedWorkspace = sessionStorage.getItem('workspace');
      if (storedWorkspace) {
        try {
          const parsed = JSON.parse(storedWorkspace);
          dispatch(setSelectedWorkspace(parsed));
        } catch {
          // ignore parse errors
        }
      } else if (workspaces && workspaces.length > 0) {
        // If no workspace in sessionStorage but we have fetched workspaces, set the first one
        console.log('Setting default workspace from fetched workspaces:', workspaces[0]);
        dispatch(setSelectedWorkspace(workspaces[0]));
        sessionStorage.setItem('workspace', JSON.stringify(workspaces[0]));
      }
    }

    const checkOnboardingStatus = async () => {
      try {
        setIsCheckingStatus(true);
        hasCheckedStatusRef.current = true;

        const status = await onboarding.checkStatus();

        if (status.isCompleted) {
          // Clear cache so ProtectedLayout will re-check
          clearOnboardingCache();
          navigate(AppRoutes.dashboard);
          return;
        }

        // Populate existing data
        // Map backend response structure to frontend form data
        const stripeConnection = (status as any).stripeConnection;
        const stripeConnected = stripeConnection?.isConnected || false;
        const stripeAccountId = stripeConnection?.stripeAccountId || stripeConnection?.accountId || '';

        // Get workspace name from status, workspace object, or use "My Workspace" as default
        // Priority: status.workspaceName > workspace?.name > "My Workspace"
        const workspaceNameFromStatus = status.workspaceName || '';
        // Get current workspace from Redux state (may have been set above)
        const currentWorkspace = workspace || (workspaces && workspaces.length > 0 ? workspaces[0] : null);
        const workspaceNameFromWorkspace = currentWorkspace?.name || '';
        const defaultWorkspaceName = 'My Workspace';
        // Use workspace name if available, otherwise use default
        const finalWorkspaceName = workspaceNameFromStatus || workspaceNameFromWorkspace || defaultWorkspaceName;

        setFormData((prev) => ({
          ...prev,
          workspaceName: finalWorkspaceName,
          stripeConnected: stripeConnected,
          stripeAccountId: stripeAccountId,
          sdkKey: status.sdkKey || '',
        }));

        // Set current step based on progress - use the saved step from backend
        setCurrentStep(status.currentStep || 1);

        setIsCheckingStatus(false);
      } catch (error) {
        console.log('No onboarding status available, starting fresh');
        // If no status available, start from step 1
        // Try to get workspace name from workspace object if available
        const currentWorkspace = workspace || (workspaces && workspaces.length > 0 ? workspaces[0] : null);
        const workspaceNameFromWorkspace = currentWorkspace?.name || 'My Workspace';

        setFormData((prev) => ({
          ...prev,
          workspaceName: workspaceNameFromWorkspace,
        }));
        setCurrentStep(1);
        setIsCheckingStatus(false);
        hasCheckedStatusRef.current = true; // Mark as checked even on error
      }
    };

    // Check onboarding status - don't wait for workspaces as they're not required for status check
    checkOnboardingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  const validateStep = (step: number) => {
    const newErrors: { [key: string]: string } = {};

    if (step === 1 && !formData.workspaceName.trim()) {
      newErrors.workspaceName = 'Workspace name is required';
    }

    if (step === 2 && !formData.stripeConnected) {
      newErrors.stripe = 'Please connect your Stripe account to continue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    console.log('📍 handleNext - Current Step:', currentStep, 'Workspace ID:', workspace?.id);
    setIsLoading(true);

    if (validateStep(currentStep)) {
      try {
        if (currentStep === 1) {
          console.log('💾 Step 1: Saving workspace name:', formData.workspaceName);
          await onboarding.saveWorkspace(formData.workspaceName);

          const { data: updatedWorkspaces } = await refetchWorkspaces();
          console.log('📦 Fetched workspaces after save:', updatedWorkspaces);

          if (updatedWorkspaces && updatedWorkspaces.length > 0) {
            const defaultWorkspace = updatedWorkspaces[0];
            console.log('✅ Setting workspace in Redux and sessionStorage:', defaultWorkspace.id);
            dispatch(setSelectedWorkspace(defaultWorkspace));
            sessionStorage.setItem('workspace', JSON.stringify(defaultWorkspace));
            console.log('✅ Workspace persisted. ID:', defaultWorkspace.id);
          }
        } else if (currentStep === 2 && !formData.stripeConnected) {
          console.log('⚠️ Step 2: Stripe not connected, cannot proceed');
          setIsLoading(false);
          return;
        } else if (currentStep === 2) {
          console.log('➡️ Step 2 → 3: Moving to SDK setup. Workspace ID:', workspace?.id);
          setCurrentStep(3);
          setIsLoading(false);
          return;
        }

        setCurrentStep((prev) => Math.min(prev + 1, 3));
      } catch (error) {
        console.error('❌ Error in handleNext:', error);
        setCurrentStep((prev) => Math.min(prev + 1, 3));
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleGenerateAPIKey = async () => {
    console.log('🚀 handleGenerateAPIKey called', {
      hasTriedBefore: hasTriedGeneratingKeyRef.current,
      hasWorkspace: !!workspace?.id,
      workspaceId: workspace?.id,
      hasSdkKey: !!formData.sdkKey,
      isGenerating: isGeneratingKey,
    });

    // Prevent duplicate generation attempts
    if (hasTriedGeneratingKeyRef.current) {
      console.log('⏭️ Already tried, skipping');
      return;
    }

    // Mark as tried NOW, before any async operations
    hasTriedGeneratingKeyRef.current = true;

    if (formData.sdkKey) {
      console.log('✅ Already have SDK key, skipping');
      return;
    }

    // ALWAYS fetch fresh workspaces at step 3 - there's only one anyway during onboarding
    console.log('📦 Fetching workspaces for step 3...');
    let workspaceId: string | undefined;

    try {
      const { data: fetchedWorkspaces } = await refetchWorkspaces();
      console.log('✅ Fetched workspaces:', fetchedWorkspaces?.length || 0);

      if (fetchedWorkspaces && fetchedWorkspaces.length > 0) {
        const firstWorkspace = fetchedWorkspaces[0];
        if (firstWorkspace.id) {
          workspaceId = firstWorkspace.id;
        } else if (firstWorkspace.SK && typeof firstWorkspace.SK === 'string' && firstWorkspace.SK.startsWith('WS#')) {
          // Extract everything after 'WS#'
          console.log('✅ Extracting workspace ID from SK:', firstWorkspace.SK);
          workspaceId = firstWorkspace.SK.split('WS#')[1];
        } else {
          workspaceId = undefined;
        }
        console.log('✅ Using workspace ID:', workspaceId);

        // Set it in Redux and sessionStorage for consistency
        dispatch(setSelectedWorkspace(firstWorkspace));
        sessionStorage.setItem('workspace', JSON.stringify(firstWorkspace));
      } else {
        console.error('❌ No workspaces found!');
        setErrors((prev) => ({ ...prev, sdkKey: 'No workspace found. Please refresh and try again.' }));
        return;
      }
    } catch (error) {
      console.error('❌ Failed to fetch workspaces:', error);
      setErrors((prev) => ({ ...prev, sdkKey: 'Failed to load workspace. Please refresh and try again.' }));
      return;
    }

    if (!workspaceId) {
      console.error('❌ No workspace ID available!');
      setErrors((prev) => ({ ...prev, sdkKey: 'No workspace ID available. Please refresh and try again.' }));
      return;
    }

    console.log('🔄 Starting API key generation with workspace:', workspaceId);
    setIsGeneratingKey(true);

    try {
      // Call the API directly with the workspaceId we just fetched
      const token = user?.signInUserSession?.idToken?.jwtToken;
      if (!token) {
        throw new Error('No authentication token');
      }

      console.log('📞 Calling .NET API /api-credentials (workspace context:', workspaceId, ')');
      const response = await fetch(`${getBackendBaseUrl()}/api-credentials`, {
        method: 'POST',
        headers: backendRequestHeaders(token),
        body: JSON.stringify({ workspaceId, label: 'Onboarding SDK Key' }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API error response:', response.status, errorText);
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      const apiKey = data?.rawKey as string | undefined;
      console.log('✅ Got API key:', apiKey ? 'YES' : 'NO');
      console.log('📦 Full response:', data);

      if (apiKey) {
        setFormData((prev) => ({
          ...prev,
          sdkKey: apiKey,
        }));
        console.log('✅ API key saved to state');
      } else {
        throw new Error('No API key in response');
      }
    } catch (error: any) {
      console.error('❌ Error generating API key:', error);

      // Better 409 detection with multiple fallbacks
      const isConflict =
        error?.response?.status === 409 ||
        error?.status === 409 ||
        error?.message?.includes('409') ||
        error?.message?.toLowerCase().includes('already exists') ||
        error?.message?.toLowerCase().includes('conflict');

      if (isConflict) {
        console.log('⚠️ 409 Conflict detected');
        setErrors((prev) => ({
          ...prev,
          sdkKey:
            'An API key has already been created for this workspace during onboarding. For security reasons, we cannot display it again. You can create a new API key from Settings → API Credentials after completing onboarding.',
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          sdkKey: `Failed to generate API key: ${error?.message || 'Unknown error'}`,
        }));
      }
    } finally {
      setIsGeneratingKey(false);
      console.log('🏁 Generation attempt complete');
    }
  };

  const copySdkKey = () => {
    navigator.clipboard.writeText(formData.sdkKey);
    setCopiedSdk(true);
    setTimeout(() => setCopiedSdk(false), 2000);
  };

  const handleCompleteOnboarding = async () => {
    setIsCompletingOnboarding(true);
    try {
      await onboarding.completeOnboarding();
      // Clear cache so ProtectedLayout will know onboarding is completed
      clearOnboardingCache();
      navigate(AppRoutes.dashboard);
    } catch (error) {
      console.log('Onboarding completed locally, redirecting to dashboard...');
      // Clear cache even on error
      clearOnboardingCache();
      // Even if there's an error, redirect to dashboard
      navigate(AppRoutes.dashboard);
    } finally {
      setIsCompletingOnboarding(false);
    }
  };

  // Show loading state while checking onboarding status
  if (isCheckingStatus) {
    return (
      <div className="min-h-screen bg-custom-silver p-4 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue mx-auto mb-4"></div>
          <p className="text-cusotm-black">Checking your account status...</p>
        </div>
      </div>
    );
  }

  const renderStepIndicator = () => (
    <div className="flex justify-center mb-8">
      <div className="flex items-center gap-6">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;

          return (
            <React.Fragment key={step.id}>
              <div className="relative flex flex-col items-center">
                <div
                  className={`relative z-10
                  w-12 h-12 rounded-full flex items-center justify-center border transition-all duration-300
                  ${
                    isActive
                      ? 'bg-white border-custom-blue text-custom-blue'
                      : isCompleted
                      ? 'bg-custom-blue border-custom-blue text-white'
                      : 'bg-custom-silver border-custom-darkSilver text-custom-darkSilver'
                  }
                `}
                >
                  {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                </div>
                <span
                  className={`mt-2 text-sm font-medium ${
                    isActive || isCompleted ? 'text-custom-blue' : 'text-custom-darkSilver'
                  }`}
                >
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  key={`divider-${step.id}`}
                  className={`w-10 h-[1px] mt-[-27px] transition-colors duration-300 ${
                    currentStep > step.id ? 'bg-custom-blue' : 'bg-custom-darkSilver'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-custom-black mb-2">Set up your workspace</h2>
        <p className="text-custom-black">Give your workspace a name to get started</p>
      </div>

      <div className="max-w-md mx-auto">
        <Input
          type="text"
          value={formData.workspaceName}
          onChange={(e) => handleInputChange('workspaceName', e.target.value)}
          label="Workspace Name"
          placeholder="My Workspace"
          error={errors.workspaceName}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-custom-black mb-2">Connect your Stripe account</h2>
        <p className="text-custom-black">Import your payment data to get comprehensive analytics</p>
      </div>

      <div className="max-w-md mx-auto">
        {!formData.stripeConnected ? (
          <div className="space-y-4">
            <StripeOAuthButton
              onSuccess={async (data: { stripeAccountId: string }) => {
                // Update local state immediately
                setFormData((prev) => ({
                  ...prev,
                  stripeConnected: true,
                  stripeAccountId: data.stripeAccountId,
                }));

                // Refetch onboarding status from backend to ensure data is in sync
                // But stay on step 2 - user can click Next button to proceed
                try {
                  const updatedStatus = await onboarding.checkStatus();
                  // Update form data with any additional status info, but don't change step
                  if (updatedStatus.sdkKey) {
                    setFormData((prev) => ({ ...prev, sdkKey: updatedStatus.sdkKey || '' }));
                  }
                  // Clear cache to ensure fresh data on next check
                  clearOnboardingCache();
                } catch (error) {
                  console.error('Failed to refresh onboarding status after Stripe connection:', error);
                  // Continue on step 2 even if status check fails
                }
              }}
              onError={(error: string) => {
                setErrors((prev) => ({ ...prev, stripe: error }));
              }}
            />

            {errors.stripe && <p className="text-sm text-custom-red text-center">{errors.stripe}</p>}
            <p className="text-xs text-custom-darkSilver text-center">
              Click the button above to securely connect your Stripe account via OAuth. This will automatically import
              your customers and invoices.
            </p>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-600" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-green-800 mb-2">Successfully Connected!</h3>
            <p className="text-green-700 mb-2">Account ID: {formData.stripeAccountId}</p>
            <p className="text-sm text-green-600">Your Stripe data is being imported automatically</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => {
    // Show loading state while generating API key (but not if there's an error)
    if ((isGeneratingKey || !formData.sdkKey) && !errors.sdkKey) {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-custom-black mb-2">SDK Integration</h2>
            <p className="text-custom-black">Generating your API key...</p>
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="border border-custom-lightSilver rounded-lg p-8 text-center">
              <LoaderCircle className="animate-spin mx-auto mb-4 text-custom-blue" size={48} />
              <p className="text-custom-darkSilver">Please wait while we generate your API key</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-custom-black mb-2">SDK Integration</h2>
          <p className="text-custom-black">Copy your API key and integrate the SDK</p>
        </div>

        <div className="max-w-2xl mx-auto space-y-6">
          {/* Show error if API key generation failed */}
          {errors.sdkKey && (
            <div className="border-2 border-amber-400 rounded-lg p-5 bg-amber-50">
              <div className="flex items-start gap-3">
                <InfoIcon size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-base font-semibold text-amber-900 mb-2">API Key Setup Notice</div>
                  <p className="text-sm text-amber-800">{errors.sdkKey}</p>
                </div>
              </div>
            </div>
          )}

          {/* API Key Section - Only show if we have the key */}
          {formData.sdkKey && (
            <div className="border border-custom-lightSilver rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-custom-black">Your API Key</div>
                <button
                  onClick={copySdkKey}
                  className="px-3 py-1.5 rounded-lg transition-colors bg-custom-blue hover:bg-custom-darkBlue text-white text-sm flex items-center gap-2"
                >
                  {copiedSdk ? (
                    <>
                      <Check size={16} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <code className="block bg-custom-silver text-custom-blue p-3 rounded-lg font-mono text-sm break-all">
                {formData.sdkKey}
              </code>
              <p className="mt-2 text-xs text-custom-darkSilver">
                Keep this key secure. It provides access to your workspace.
              </p>
            </div>
          )}
          {/* Integration Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <h4 className="font-semibold text-custom-black mb-4 flex items-center gap-2">
              <InfoIcon size={20} className="text-custom-blue" />
              Quick Start Guide
            </h4>
            <div className="text-sm text-custom-black space-y-4">
              {/* Step 1 */}
              <div className="flex gap-3">
                <span className="font-bold text-custom-blue text-lg flex-shrink-0">1.</span>
                <div className="flex-1">
                  <div className="font-semibold mb-2">Install the SDK</div>
                  <code className="block bg-white px-3 py-2 rounded text-xs font-mono border border-blue-200">
                    npm install @churnsignal/churnsignal-sdk-web
                  </code>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-3">
                <span className="font-bold text-custom-blue text-lg flex-shrink-0">2.</span>
                <div className="flex-1">
                  <div className="font-semibold mb-2">Initialize the SDK</div>
                  <p className="text-custom-darkSilver mb-2">
                    Add the initialization code to your app entry point (e.g., index.js, App.tsx):
                  </p>
                  <code className="block bg-white px-3 py-2 rounded text-xs font-mono border border-blue-200 whitespace-pre break-all">
                    {`import { initialise } from '@churnsignal/churnsignal-sdk-web';

const success = initialise(${JSON.stringify(formData.sdkKey)});
if (success) {
  console.log('ChurnSignal ready!');
}`}
                  </code>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-3">
                <span className="font-bold text-custom-blue text-lg flex-shrink-0">3.</span>
                <div className="flex-1">
                  <div className="font-semibold mb-2">Track User Events</div>
                  <p className="text-custom-darkSilver mb-2">
                    Track important user actions throughout your application:
                  </p>
                  <code className="block bg-white px-3 py-2 rounded text-xs font-mono border border-blue-200 whitespace-pre">
                    {`import { track } from '@churnsignal/churnsignal-sdk-web';

// Track with user email and properties
track('page_viewed', 
  { page: '/home' }, 
  'user@example.com'
);

// Track user actions
track('feature_used', 
  { feature: 'export' }, 
  'user@example.com'
);`}
                  </code>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-3">
                <span className="font-bold text-custom-blue text-lg flex-shrink-0">4.</span>
                <div className="flex-1">
                  <div className="font-semibold mb-2">Key Events to Track</div>
                  <ul className="text-custom-darkSilver space-y-1 ml-4 list-disc">
                    <li>
                      <code className="text-xs bg-white px-1 py-0.5 rounded">page_viewed</code> - Page navigation
                    </li>
                    <li>
                      <code className="text-xs bg-white px-1 py-0.5 rounded">feature_used</code> - Feature engagement
                    </li>
                    <li>
                      <code className="text-xs bg-white px-1 py-0.5 rounded">purchase_completed</code> - Revenue events
                    </li>
                    <li>
                      <code className="text-xs bg-white px-1 py-0.5 rounded">error_occurred</code> - User issues
                    </li>
                    <li>
                      <code className="text-xs bg-white px-1 py-0.5 rounded">support_contacted</code> - Support
                      interactions
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex gap-3">
                <span className="font-bold text-custom-blue text-lg flex-shrink-0">5.</span>
                <div className="flex-1">
                  <div className="font-semibold mb-2">Monitor Your Dashboard</div>
                  <p className="text-custom-darkSilver">
                    View real-time churn risk analytics, user behavior patterns, and receive proactive alerts when users
                    show signs of churning.
                  </p>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-white rounded-lg p-3 border border-blue-200 mt-4">
                <div className="flex items-start gap-2">
                  <InfoIcon size={16} className="text-custom-blue mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-custom-darkSilver">
                    <span className="font-semibold text-custom-black">Pro Tip:</span> The SDK automatically extracts
                    your workspace ID, tenant ID, and base URL from the API key. Always include the user&apos;s email
                    when tracking events for accurate churn prediction.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  return (
    <div
      className="min-h-screen bg-custom-silver p-4 pb-24 flex items-center justify-center"
      style={{ minHeight: '100dvh' }}
    >
      <div className="max-w-4xl w-full">
        <div className="bg-white rounded-xl shadow-md p-8 flex flex-col max-h-[calc(100dvh-40px)] overflow-y-auto">
          <div className="flex-shrink-0 mb-8">{renderStepIndicator()}</div>

          <div className="flex-1 py-4 min-h-0 overflow-y-auto">{renderCurrentStep()}</div>

          <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-t-custom-mediumSilver flex-shrink-0">
            <Button
              onClick={async () => {
                try {
                  // Wait for signOut to complete before navigating
                  await signOut();
                  // Clear onboarding cache to prevent redirect back to onboarding
                  clearOnboardingCache();
                  navigate(AppRoutes.signIn, { replace: true });
                } catch (error: any) {
                  console.error('Logout error:', error);
                  // Navigate anyway to ensure user can sign in again
                  navigate(AppRoutes.signIn, { replace: true });
                }
              }}
              variant="transparent"
              disabled={isLoading}
              className="mr-auto"
            >
              Logout
            </Button>

            {currentStep > 1 && (
              <Button onClick={handlePrevious} variant="transparent" disabled={isLoading}>
                Prev
              </Button>
            )}

            {currentStep < 3 ? (
              <Button onClick={handleNext} disabled={isLoading || (currentStep === 2 && !formData.stripeConnected)}>
                Next
                {isLoading && <LoaderCircle height={20} className="animate-spin" />}
              </Button>
            ) : (
              <Button
                onClick={handleCompleteOnboarding}
                disabled={isCompletingOnboarding || isGeneratingKey}
                isLoading={isCompletingOnboarding}
              >
                Complete setup
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
